import React from 'react';

import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseCircleOutlined,
  MinusCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Tag, theme } from 'antd';
import styled from 'styled-components';

import type { IOtaStepProgress } from '../api/getOtaExecutionHistory';
import { OTA_STEP_LABEL } from '../helpers/ota';
import {
  OTA_EXECUTION_STATUS,
  OTA_STEP_NAME,
  OTA_STEP_STATUS,
  OtaExecutionStatus,
  OtaStepStatus,
} from '../types';
import { formatDuration, formatISODate } from '@/helpers/dates';

interface IBoxOtaHistoricalStepsProps {
  steps: IOtaStepProgress[];
  executionStatus: OtaExecutionStatus;
}

const STEP_TAG_COLOR: Record<OtaStepStatus, string> = {
  [OTA_STEP_STATUS.COMPLETED]: 'success',
  [OTA_STEP_STATUS.FAILED]: 'error',
  [OTA_STEP_STATUS.IN_PROGRESS]: 'processing',
  [OTA_STEP_STATUS.PENDING]: 'default',
  [OTA_STEP_STATUS.UNSPECIFIED]: 'default',
};

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 2.75rem;
`;

const StepRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const StepIconColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 1.25rem;
  flex-shrink: 0;
`;

const StepTail = styled.div<{ $color: string }>`
  flex: 1;
  width: 1px;
  background-color: ${({ $color }) => $color};
  min-height: 1.5rem;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding-bottom: 0.75rem;
`;

const StepTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepName = styled.span<{ $failed?: boolean; $color?: string }>`
  font-weight: 600;
  color: ${({ $failed, $color }) => ($failed && $color ? $color : 'inherit')};
`;

const StepStartedAt = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 0.8rem;
`;

const ICON_STYLE = { fontSize: '1.25rem' };

export const BoxOtaHistoricalSteps = ({
  steps,
  executionStatus,
}: IBoxOtaHistoricalStepsProps) => {
  const { token } = theme.useToken();
  const isCancelled = executionStatus === OTA_EXECUTION_STATUS.CANCELLED;
  const filteredSteps = steps.filter((step) => step.step !== OTA_STEP_NAME.END);

  const isCancelledStep = (step: IOtaStepProgress): boolean =>
    isCancelled &&
    (step.status === OTA_STEP_STATUS.IN_PROGRESS ||
      step.status === OTA_STEP_STATUS.PENDING);

  const stepIconMap: Record<OtaStepStatus, React.ReactNode> = {
    [OTA_STEP_STATUS.COMPLETED]: (
      <CheckCircleFilled style={{ ...ICON_STYLE, color: token.colorSuccess }} />
    ),
    [OTA_STEP_STATUS.FAILED]: (
      <CloseCircleFilled style={{ ...ICON_STYLE, color: token.colorError }} />
    ),
    [OTA_STEP_STATUS.IN_PROGRESS]: (
      <SyncOutlined spin style={{ ...ICON_STYLE, color: token.colorPrimary }} />
    ),
    [OTA_STEP_STATUS.PENDING]: (
      <MinusCircleOutlined
        style={{ ...ICON_STYLE, color: token.colorTextDisabled }}
      />
    ),
    [OTA_STEP_STATUS.UNSPECIFIED]: (
      <MinusCircleOutlined
        style={{ ...ICON_STYLE, color: token.colorTextDisabled }}
      />
    ),
  };

  const getStepIcon = (step: IOtaStepProgress): React.ReactNode => {
    if (isCancelledStep(step)) {
      return (
        <CloseCircleOutlined
          style={{ ...ICON_STYLE, color: token.colorWarning }}
        />
      );
    }

    return stepIconMap[step.status] ?? stepIconMap[OTA_STEP_STATUS.UNSPECIFIED];
  };

  return (
    <StepList data-testid="ota-history-steps">
      {filteredSteps.map((step, index) => {
        const isFailed = step.status === OTA_STEP_STATUS.FAILED;
        const isLast = index === filteredSteps.length - 1;
        const label = OTA_STEP_LABEL[step.step] ?? step.step;
        const showDuration =
          step.duration_seconds !== undefined &&
          step.status !== OTA_STEP_STATUS.IN_PROGRESS;
        const tagColor =
          STEP_TAG_COLOR[step.status] ??
          STEP_TAG_COLOR[OTA_STEP_STATUS.UNSPECIFIED];

        return (
          <StepRow key={`${step.step}-${index}`}>
            <StepIconColumn>
              {getStepIcon(step)}
              {!isLast && <StepTail $color={token.colorBorderSecondary} />}
            </StepIconColumn>
            <StepContent>
              <StepTitleRow>
                <StepName $failed={isFailed} $color={token.colorError}>
                  {label}
                </StepName>
                {showDuration && (
                  <Tag color={tagColor}>
                    Duration: {formatDuration(step.duration_seconds!)}
                  </Tag>
                )}
              </StepTitleRow>
              {step.started_at && (
                <StepStartedAt>
                  Started: {formatISODate(step.started_at)}
                </StepStartedAt>
              )}
            </StepContent>
          </StepRow>
        );
      })}
    </StepList>
  );
};
